// Catálogo de tutoriais do produto ativo + "já vi" do usuário (PO1-3506).
//
// Montado uma vez no layout Admin: carrega GET /treinamento/tutoriais?produto=
// e GET /treinamento/vistos quando o produto ativo muda. Quem consome é o ícone
// de ajuda do cabeçalho (components/AjudaTela). A Central de Treinamento NÃO
// usa este contexto: ela carrega a própria lista (no Admin pede tudo).
//
// Fora do provider, `useTreinamento()` devolve um valor vazio em vez de
// explodir: o cabeçalho é renderizado em testes e telas sem o provider.
//
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useActiveProduct } from "../hooks/activeProduct";
import { useAuth } from "../hooks/auth";
import { listarTutoriais, listarVistos, marcarVisto as marcarVistoApi } from "../services/treinamentoApi";

const VAZIO = {
  tutoriais: [],
  vistos: [],
  carregando: false,
  produto: null,
  marcarVisto: () => Promise.resolve(),
  recarregar: () => Promise.resolve(),
};

const TreinamentoContext = createContext(VAZIO);

export function TreinamentoProvider({ children }) {
  const { activeProduct } = useActiveProduct();
  const { user } = useAuth();
  const [tutoriais, setTutoriais] = useState([]);
  const [vistos, setVistos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const montado = useRef(true);
  const pedido = useRef(0);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const carregar = useCallback(async () => {
    if (!user || !activeProduct) {
      setTutoriais([]);
      return;
    }
    const atual = pedido.current + 1;
    pedido.current = atual;
    setCarregando(true);
    try {
      const [lista, vistosLista] = await Promise.all([
        listarTutoriais(activeProduct),
        listarVistos().catch(() => []),
      ]);
      if (!montado.current || pedido.current !== atual) return;
      setTutoriais(lista);
      setVistos(vistosLista);
    } catch (e) {
      // Silencioso: sem catálogo o ícone de ajuda só não aparece.
      if (montado.current && pedido.current === atual) setTutoriais([]);
    } finally {
      if (montado.current && pedido.current === atual) setCarregando(false);
    }
  }, [user, activeProduct]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const marcarVisto = useCallback(async (codigo) => {
    if (!codigo) return;
    setVistos((prev) => (prev.indexOf(codigo) === -1 ? prev.concat([codigo]) : prev));
    try {
      await marcarVistoApi(codigo);
    } catch (e) {
      // O aviso já sumiu nesta sessão; na próxima carga o servidor diz se gravou.
    }
  }, []);

  const value = useMemo(
    () => ({ tutoriais, vistos, carregando, produto: activeProduct || null, marcarVisto, recarregar: carregar }),
    [tutoriais, vistos, carregando, activeProduct, marcarVisto, carregar],
  );

  return <TreinamentoContext.Provider value={value}>{children}</TreinamentoContext.Provider>;
}

export function useTreinamento() {
  return useContext(TreinamentoContext) || VAZIO;
}

export default TreinamentoContext;
